import { useState } from "react";
import { Link } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Building2,
  Plus,
  RotateCcw,
  Search,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { type PerkProgram, type ProgramStatus } from "./programs/mock-programs";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { useDebounce } from "@/hooks/use-debounce";
import { ProgramRowActions } from "./programs/ProgramRowActions";

type SearchType = "programName" | "programCode";
type StatusTab = "all" | "Active" | "Draft" | "Paused" | "Closed";

const STATUS_TABS: { id: StatusTab; label: string }[] = [
  { id: "all", label: "All programs" },
  { id: "Active", label: "Active" },
  { id: "Draft", label: "Draft" },
  { id: "Paused", label: "Paused" },
  { id: "Closed", label: "Closed" },
];

const CREATED_WITHIN: Record<string, "7" | "30" | "90" | undefined> = {
  all: undefined,
  "7days": "7",
  "30days": "30",
  "90days": "90",
};

function StatusBadge({ status }: { status: ProgramStatus }) {
  const styles: Record<ProgramStatus, string> = {
    Active: "bg-green-50 text-green-700",
    Draft: "bg-slate-100 text-slate-600",
    Paused: "bg-amber-50 text-amber-700",
    Closed: "bg-red-50 text-red-700",
  };
  const dots: Record<ProgramStatus, string> = {
    Active: "bg-green-500",
    Draft: "bg-slate-400",
    Paused: "bg-amber-500",
    Closed: "bg-red-500",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        styles[status],
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", dots[status])} />
      {status}
    </span>
  );
}

export default function ProgramSetup() {
  const queryClient = useQueryClient();
  const [searchType, setSearchType] = useState<SearchType>("programName");
  const [searchQuery, setSearchQuery] = useState("");
  const search = useDebounce(searchQuery.trim(), 300);
  const [dateRange, setDateRange] = useState("all");
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<PerkProgram | null>(
    null,
  );

  const query = {
    search: search || undefined,
    searchBy: search ? (searchType === "programName" ? "name" : "code") : undefined,
    status: statusTab === "all" ? undefined : statusTab.toLowerCase(),
    createdWithin: CREATED_WITHIN[dateRange],
    page: page + 1,
    pageSize: rowsPerPage,
  } as const;
  const programsQuery = useQuery({
    queryKey: programKeys.list(query),
    queryFn: () => ProgramsApi.list(query),
    placeholderData: keepPreviousData,
  });
  const paginatedPrograms = programsQuery.data?.items ?? [];
  const filteredTotal = programsQuery.data?.total ?? 0;
  const apiStats = programsQuery.data?.stats;
  const stats = {
    total: apiStats?.total ?? 0,
    active: apiStats?.active ?? 0,
    draft: apiStats?.draft ?? 0,
    members: apiStats?.linked_members ?? 0,
  };

  const totalPages = Math.max(1, Math.ceil(filteredTotal / rowsPerPage));
  const currentPage = Math.min(page, totalPages - 1);

  const resetFilters = () => {
    setSearchQuery("");
    setSearchType("programName");
    setDateRange("all");
    setStatusTab("all");
    setPage(0);
  };

  const deleteMutation = useMutation({
    mutationFn: (program: PerkProgram) => ProgramsApi.remove(program.id),
    onSuccess: (_, program) => {
      toast.success(`Program ${program.name} deleted`);
      queryClient.invalidateQueries({ queryKey: programKeys.all });
      setDeleteDialogOpen(false);
      setProgramToDelete(null);
      setPage(0);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not delete the program")),
  });

  const confirmDeleteProgram = () => {
    if (programToDelete) deleteMutation.mutate(programToDelete);
  };

  return (
    <div className="space-y-6 pb-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Program <span className="mx-1 text-slate-300">/</span>{" "}
            <span className="text-slate-700">Program Setup</span>
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Program Setup
          </h1>
          <p className="mt-1 text-muted-foreground">
            Create and manage Perk Point loyalty programs
          </p>
        </div>
        <Button asChild className="bg-green-600 text-white hover:bg-green-700">
          <Link to="/perkpoint/program/setup/create">
            <Plus className="mr-2 h-4 w-4" /> Add Program
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: "Total programs",
            value: stats.total,
            icon: Building2,
          },
          {
            label: "Active programs",
            value: stats.active,
            icon: Building2,
          },
          {
            label: "Draft programs",
            value: stats.draft,
            icon: Building2,
          },
          {
            label: "Linked members",
            value: stats.members.toLocaleString(),
            icon: Users,
          },
        ].map((item) => (
          <Card key={item.label} className="border-slate-200 shadow-sm">
            <CardContent className="flex items-center justify-between p-5">
              <div>
                <p className="text-sm text-muted-foreground">{item.label}</p>
                <p className="mt-1 text-3xl font-bold text-slate-900">
                  {item.value}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <item.icon className="h-5 w-5" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setStatusTab(tab.id);
              setPage(0);
            }}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
              statusTab === tab.id
                ? "border-green-600 bg-green-50 text-green-700"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm lg:flex-row lg:items-center">
        <Select
          value={searchType}
          onValueChange={(value: SearchType) => {
            setSearchType(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="h-10 w-full bg-white lg:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="programName">Program Name</SelectItem>
            <SelectItem value="programCode">Program Code</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            placeholder={
              searchType === "programName"
                ? "Search by program name..."
                : "Search by program code..."
            }
            className="h-10 bg-white pl-9"
          />
        </div>

        <Select
          value={dateRange}
          onValueChange={(value) => {
            setDateRange(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="h-10 w-full bg-white lg:w-44">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="7days">Last 7 Days</SelectItem>
            <SelectItem value="30days">Last 30 Days</SelectItem>
            <SelectItem value="90days">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="outline"
          className="h-10 bg-white"
          onClick={resetFilters}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead>Created</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>CPP</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>City / Country</TableHead>
                  <TableHead className="w-12 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPrograms.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      {programsQuery.isLoading
                        ? "Loading programs…"
                        : programsQuery.isError
                          ? apiErrorMessage(programsQuery.error, "Could not load programs")
                          : "No programs match the selected filters."}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPrograms.map((program) => (
                    <TableRow key={program.id}>
                      <TableCell className="whitespace-nowrap text-sm text-slate-600">
                        {program.created}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-green-50 text-sm font-semibold text-green-700">
                            {program.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {program.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {program.clientName}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                          {program.programCode}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={program.status} />
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {program.currency}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {program.costPerPoint}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-800">
                          <Users className="h-3 w-3" />
                          {program.membersCount}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-700">
                          {program.city}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {program.country}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <ProgramRowActions
                          program={program}
                          onDelete={program.status !== "Draft" ? undefined : (target) => {
                            setProgramToDelete(target);
                            setDeleteDialogOpen(true);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {paginatedPrograms.length} of {filteredTotal}{" "}
              programs
            </p>
            <div className="flex items-center gap-3">
              <Select
                value={String(rowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setPage(0);
                }}
              >
                <SelectTrigger className="h-9 w-[90px] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 bg-white"
                  disabled={currentPage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 bg-green-600 text-white hover:bg-green-700"
                >
                  {currentPage + 1}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 bg-white"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{programToDelete?.name}
              &quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteProgram}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
